/**
 * Smossie Puddles — image cyclers.
 *
 * Two custom elements share one clock and one colour picker:
 *
 *   <deck-cycler>   the hero. Two stacked layers; the front one pops off to the
 *                   right and the layer behind it becomes the new front, like
 *                   dealing a card to the back of the deck. Carries the
 *                   progress indicator.
 *   <flip-cycler>   the plain one. A single frame that swaps images in place.
 *
 * Both are progressive: the markup renders a valid first slide on its own, and
 * these classes only ever add motion on top of it.
 */

(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /**
   * Picks layer colours so the same one never appears twice in a row and no
   * colour is ever locked out.
   *
   * The naive approach — remove used colours from the pool — shrinks the pool
   * as slides advance and can permanently exclude a colour. Instead every
   * colour stays in the pool forever and carries a cooldown: the most recently
   * used colour is unpickable, recent ones are unlikely, and everything else is
   * equally likely. So with six colours and five images, all six still appear
   * over time, and yellow never follows yellow.
   */
  class ColourBag {
    constructor(colours) {
      this.colours = colours.filter(Boolean);
      /** Tick each colour was last used on; starts "long ago". */
      this.age = this.colours.map(() => Number.MIN_SAFE_INTEGER);
      this.tick = 0;
    }

    next() {
      if (this.colours.length === 0) return null;
      if (this.colours.length === 1) return this.colours[0];

      this.tick += 1;

      // Cooldown spans at most half the pool, so a pick is always available.
      const cooldown = Math.max(1, Math.floor(this.colours.length / 2));
      const weights = this.colours.map((_, i) => {
        const since = this.tick - this.age[i];
        if (since <= 1) return 0; // used last slide: never twice in a row
        if (since <= cooldown) return 0.15; // recent: possible but unlikely
        return 1;
      });

      const total = weights.reduce((sum, w) => sum + w, 0);
      // Every weight zero can only happen with a pool of one, handled above.
      let roll = Math.random() * total;
      let index = weights.findIndex((w) => (roll -= w) < 0);
      if (index < 0) index = Math.max(0, weights.findIndex((w) => w > 0));

      this.age[index] = this.tick;
      return this.colours[index];
    }
  }

  /** Shared clock plumbing: autoplay that pauses off-screen and on hover. */
  class CyclerBase extends HTMLElement {
    connectedCallback() {
      this.interval = (parseFloat(this.dataset.interval) || 5) * 1000;
      this.animation = this.dataset.animation || 'deck';
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.index = 0;
      this.busy = false;

      this.colours = new ColourBag((this.dataset.colours || '').split(',').map((c) => c.trim()));

      this.setup();
      this.addEventListener('click', (event) => {
        const next = event.target.closest('[data-deck-next]');
        if (!next) return;
        event.preventDefault();
        event.stopPropagation();
        this.advance();
      });

      // A single image (or none) is a static composition, not a slideshow.
      this.canPlay = this.slides.length > 1 && !REDUCED_MOTION.matches;
      if (!this.canPlay) {
        this.classList.add('is-static');
        return;
      }

      this.observer = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.play() : this.pause()),
        { threshold: 0.25 }
      );
      this.observer.observe(this);

      this.onMotionChange = () => {
        if (REDUCED_MOTION.matches) this.pause();
      };
      REDUCED_MOTION.addEventListener('change', this.onMotionChange);
    }

    disconnectedCallback() {
      this.pause();
      this.observer?.disconnect();
      REDUCED_MOTION.removeEventListener('change', this.onMotionChange);
    }

    play() {
      if (!this.canPlay || this.timer) return;
      this.startProgress();
      this.timer = setInterval(() => this.advance(), this.interval);
    }

    pause() {
      clearInterval(this.timer);
      this.timer = null;
      this.pauseProgress();
    }

    /* Overridden by subclasses. */
    setup() {}
    advance() {}
    startProgress() {}
    pauseProgress() {}
  }

  /* ------------------------------------------------------------ deck ---- */

  /**
   * The hero deck.
   *
   * The trick that makes this work with only two DOM layers: each layer is a
   * coloured panel that *also* holds an image. The back layer shows its colour
   * because its image is transparent. When the front layer pops away, the back
   * layer fades its image in and becomes the front, and the popped layer is
   * recycled to the back with the next image and a fresh colour already set.
   * So there is never a moment where a layer has to be created or destroyed.
   */
  class DeckCycler extends CyclerBase {
    setup() {
      this.front = this.querySelector('[data-layer="front"]');
      this.back = this.querySelector('[data-layer="back"]');
      this.dots = this.querySelector('[data-indicator]');

      if (this.back) this.back.style.setProperty('--layer-color', this.colours.next());

      // With fewer than two images the indicator would be lying about progress.
      if (this.slides.length < 2 && this.dots) this.dots.hidden = true;
    }

    advance() {
      if (this.busy || !this.front || !this.back) return;
      this.busy = true;

      const nextIndex = (this.index + 1) % this.slides.length;
      const afterIndex = (nextIndex + 1) % this.slides.length;

      // Back layer is already holding the next image; reveal it as it rises.
      this.back.dataset.state = 'rising';
      this.front.dataset.state = 'popping';

      let settled = false;
      const settle = () => {
        // transitionend fires once per animated property, and the safety timer
        // below can fire too. Only the first caller may finish the deal.
        if (settled) return;
        settled = true;

        // Swap roles rather than moving nodes, so nothing reflows.
        this.front.dataset.layer = 'back';
        this.back.dataset.layer = 'front';
        [this.front, this.back] = [this.back, this.front];

        this.front.dataset.state = 'current';

        // Recycle the popped layer to the back with the image after next.
        this.back.dataset.state = 'waiting';
        this.setLayerImage(this.back, afterIndex);
        this.back.style.setProperty('--layer-color', this.colours.next());

        this.index = nextIndex;
        this.busy = false;
        this.restartProgress();
      };

      this.front.addEventListener('transitionend', settle, { once: true });
      // Belt and braces: if the transition never fires we still move on.
      setTimeout(settle, 900);
    }

    setLayerImage(layer, index) {
      const source = this.slides[index];
      const img = layer.querySelector('img');
      if (!source || !img) return;
      const from = source.querySelector('img') || source;
      img.src = from.dataset.src || from.getAttribute('src') || '';
      img.srcset = from.dataset.srcset || from.getAttribute('srcset') || '';
      img.alt = from.getAttribute('alt') || '';
    }

    /* --- indicator ------------------------------------------------------ */

    /**
     * Two pills. The leading one fills left-to-right over the slide duration,
     * then slides out to the left while the trailing one takes its place and a
     * fresh pill is appended behind. It reads as an endless queue, which is
     * true: the deck loops, so a count would be meaningless.
     */
    startProgress() {
      if (!this.dots) return;
      // Skip pills on their way out; the lead is the first one still in the row.
      const lead = this.dots.querySelector('[data-dot]:not([data-dot="leaving"])');
      if (!lead) return;

      // Resuming after a pause picks up where the fill was frozen.
      const done = parseFloat(lead.dataset.progress) || 0;
      delete lead.dataset.progress;

      lead.dataset.dot = 'active';
      lead.style.setProperty('--fill-duration', `${this.interval * (1 - done)}ms`);

      const fill = lead.querySelector('[data-fill]');
      if (!fill) return;
      fill.style.setProperty('--fill-from', `${done * 100}%`);
      fill.style.animation = '';
      fill.style.width = '';
      // Force a reflow so the animation replays from the new start point.
      void fill.offsetWidth;
    }

    pauseProgress() {
      const lead = this.dots?.querySelector('[data-dot="active"]');
      const fill = lead?.querySelector('[data-fill]');
      if (!fill) return;

      // Freeze the fill where it is, and remember how far it got.
      const done = fill.getBoundingClientRect().width / lead.getBoundingClientRect().width;
      lead.dataset.progress = Math.min(done, 1).toFixed(3);
      fill.style.animation = 'none';
      fill.style.width = `${done * 100}%`;
    }

    restartProgress() {
      if (!this.dots) return;
      // A spent pill that is still animating out must not be re-elected lead.
      const lead = this.dots.querySelector('[data-dot]:not([data-dot="leaving"])');
      if (lead) {
        lead.dataset.dot = 'leaving';
        lead.addEventListener('transitionend', () => lead.remove(), { once: true });
        setTimeout(() => lead.remove(), 600);
      }
      // Backfill so the row always holds two.
      const pill = document.createElement('span');
      pill.dataset.dot = 'idle';
      pill.innerHTML = '<span data-fill></span>';
      this.dots.append(pill);

      requestAnimationFrame(() => this.startProgress());
    }
  }

  /* ------------------------------------------------------------ flip ---- */

  /** The plain cycler: one frame, images swap in place. */
  class FlipCycler extends CyclerBase {
    setup() {
      this.slides.forEach((slide, i) => {
        slide.dataset.state = i === 0 ? 'current' : 'waiting';
      });
      if (this.dataset.tintLayer === 'true') {
        this.style.setProperty('--layer-color', this.colours.next());
      }
    }

    advance() {
      if (this.busy) return;
      this.busy = true;

      const current = this.slides[this.index];
      const nextIndex = (this.index + 1) % this.slides.length;
      const next = this.slides[nextIndex];

      current.dataset.state = 'leaving';
      next.dataset.state = 'current';

      setTimeout(() => {
        current.dataset.state = 'waiting';
        this.index = nextIndex;
        this.busy = false;
      }, 620);
    }
  }

  /**
   * One Suit keeps two cards visible (front and back), but advances both cards
   * through the slide blocks as pairs. This lets the merchant use the existing
   * four image blocks without losing the front/back composition.
   */
  class OneSuitCycler extends HTMLElement {
    connectedCallback() {
      this.sources = [...this.querySelectorAll('[data-slide]')];
      this.front = this.querySelector('[data-suit-image="front"]');
      this.back = this.querySelector('[data-suit-image="back"]');
      this.index = 0;
      this.interval = (parseFloat(this.dataset.interval) || 6) * 1000;
      this.timer = null;
      this.reducedMotion = REDUCED_MOTION.matches;

      if (this.sources.length < 3 || !this.front || !this.back || this.reducedMotion) return;

      this.observer = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.play() : this.pause()),
        { threshold: 0.25 },
      );
      this.observer.observe(this);
      this.onMotionChange = () => {
        this.reducedMotion = REDUCED_MOTION.matches;
        this.reducedMotion ? this.pause() : this.play();
      };
      REDUCED_MOTION.addEventListener('change', this.onMotionChange);
    }

    disconnectedCallback() {
      this.pause();
      this.observer?.disconnect();
      REDUCED_MOTION.removeEventListener('change', this.onMotionChange);
    }

    play() {
      if (this.timer || this.reducedMotion) return;
      this.timer = setInterval(() => this.advance(), this.interval);
    }

    pause() {
      clearInterval(this.timer);
      this.timer = null;
    }

    advance() {
      const pairCount = Math.ceil(this.sources.length / 2);
      this.index = (this.index + 1) % pairCount;
      const sourceIndex = this.index * 2;
      this.swap(this.front, this.sources[sourceIndex]);
      this.swap(this.back, this.sources[(sourceIndex + 1) % this.sources.length]);
    }

    swap(target, source) {
      if (!target || !source) return;
      target.classList.add('is-cycling');
      target.src = source.dataset.src || source.getAttribute('src') || '';
      target.srcset = source.dataset.srcset || source.getAttribute('srcset') || '';
      target.alt = source.getAttribute('alt') || '';
      target.addEventListener('load', () => target.classList.remove('is-cycling'), { once: true });
    }
  }

  if (!customElements.get('deck-cycler')) customElements.define('deck-cycler', DeckCycler);
  if (!customElements.get('flip-cycler')) customElements.define('flip-cycler', FlipCycler);
  if (!customElements.get('onesuit-cycler')) customElements.define('onesuit-cycler', OneSuitCycler);
})();
